import React, { useState } from "react";
import { Plus, Lock, Pencil, Trash2, Check, ListTodo, Loader, CheckCircle2, Sparkles, CalendarClock, Flag, RotateCcw } from "lucide-react";
import { useData } from "@/context/DataContext";
import { PageHeader, EventSelector, KpiCard, ProgressBar, NoEvent } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import client from "@/lib/api";
import { taskUrgency, PRIORITY_DOT, formatDate } from "@/lib/format";
import { toast } from "sonner";

const EMPTY = { title: "", description: "", taskType: "deadline", deadline: "", scheduledDate: "", startTime: "", endTime: "", responsible: "", responsibleEmail: "", priority: "medium", comments: "" };
const PRIO_LABEL = { high: "Haute", medium: "Moyenne", low: "Basse" };
const PRIO_ORDER = { high: 0, medium: 1, low: 2 };

export default function Tasks() {
  const { selectedEvent, tasksByEvent, addTask, updateTask, deleteTask } = useData();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [tab, setTab] = useState("active");
  const [typeFilter, setTypeFilter] = useState("all");
  const [sortBy, setSortBy] = useState("chrono");
  const [aiOpen, setAiOpen] = useState(false);
  const [aiBrief, setAiBrief] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiTasks, setAiTasks] = useState([]);
  const [aiSel, setAiSel] = useState({});

  const notify = async (taskId, kind) => { try { await client.post("/tasks/notify", { taskId, kind }); } catch { /* noop */ } };

  const generateAi = async () => {
    if (!aiBrief.trim()) { toast.error("Décrivez votre événement"); return; }
    setAiLoading(true);
    try {
      const { data } = await client.post("/ai/suggest-tasks", { eventId: selectedEvent.id, brief: aiBrief });
      setAiTasks(data.tasks || []);
      setAiSel(Object.fromEntries((data.tasks || []).map((_, i) => [i, true])));
      if (!(data.tasks || []).length) toast.info("Aucune tâche générée, reformulez votre brief");
    } catch { toast.error("L'assistant IA est momentanément indisponible"); } finally { setAiLoading(false); }
  };
  const addAiTasks = async () => {
    const chosen = aiTasks.filter((_, i) => aiSel[i]);
    for (const t of chosen) {
      await addTask({
        title: t.title,
        description: t.description,
        comments: t.comment || "",
        category: t.category || "administratif",
        priority: t.priority,
        taskType: "deadline",
        deadline: t.dueDate || t.deadline || "",
        responsible: "Assistant IA",
        status: "in-progress",
        eventId: selectedEvent.id,
      });
    }
    toast.success(`${chosen.length} tâche(s) ajoutée(s)`);
    setAiOpen(false); setAiBrief(""); setAiTasks([]); setAiSel({});
  };

  if (!selectedEvent) return (<><PageHeader title="Gestion des tâches" /><NoEvent /></>);

  const tasks = tasksByEvent(selectedEvent.id);
  const done = tasks.filter((t) => t.status === "done");
  const inProgress = tasks.filter((t) => t.status !== "done");
  const scheduled = tasks.filter((t) => t.taskType === "scheduled");
  const completion = tasks.length ? Math.round((done.length / tasks.length) * 100) : 0;

  const dateOf = (t) => t.taskType === "scheduled" ? (t.scheduledDate || "") : (t.deadline || "");
  const list = (tab === "active" ? inProgress : done)
    .filter((t) => typeFilter === "all" || t.taskType === typeFilter || (typeFilter === "deadline" && !t.taskType))
    .sort((a, b) => sortBy === "priority" ? (PRIO_ORDER[a.priority] ?? 1) - (PRIO_ORDER[b.priority] ?? 1) : (dateOf(a) || "9999").localeCompare(dateOf(b) || "9999"));

  const openNew = () => { setForm(EMPTY); setEditId(null); setShowForm(true); };
  const openEdit = (t) => { setForm({ ...EMPTY, ...t }); setEditId(t.id); setShowForm(true); };

  const submit = async () => {
    if (!form.title) { toast.error("Titre requis"); return; }
    const payload = { ...form, eventId: selectedEvent.id };
    if (editId) {
      const updated = await updateTask(editId, payload);
      await notify(updated.id, "status");
      toast.success("Tâche modifiée · responsable(s) notifié(s)");
    } else {
      const created = await addTask({ ...payload, status: "in-progress" });
      await notify(created.id, "created");
      toast.success("Tâche créée · responsable(s) notifié(s)");
    }
    setShowForm(false); setForm(EMPTY); setEditId(null);
  };

  const complete = async (t) => { const u = await updateTask(t.id, { ...t, status: "done" }); await notify(u.id, "status"); toast.success("Tâche terminée · responsable(s) notifié(s)"); };
  const reopen = async (t) => { const u = await updateTask(t.id, { ...t, status: "in-progress" }); await notify(u.id, "status"); toast.success("Tâche réouverte (En cours)"); };
  const remove = async (id) => { await deleteTask(id); toast.success("Tâche supprimée"); };

  return (
    <>
      <PageHeader title="Gestion des tâches" actions={<div className="flex gap-2 flex-wrap"><EventSelector /><Button variant="outline" onClick={() => setAiOpen(true)} data-testid="ai-tasks-button"><Sparkles className="h-4 w-4 mr-1" />Assistant IA</Button><Button onClick={openNew} data-testid="new-task-button"><Plus className="h-4 w-4 mr-1" />Nouvelle tâche</Button></div>} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiCard icon={ListTodo} tint="indigo" label="Total" value={tasks.length} />
        <KpiCard icon={Loader} tint="amber" label="En cours" value={inProgress.length} />
        <KpiCard icon={CheckCircle2} tint="emerald" label="Terminées" value={done.length} />
        <KpiCard icon={CalendarClock} tint="violet" label="Planifiées" value={scheduled.length} />
      </div>

      <div className="bg-card rounded-xl border border-border p-5 mb-6">
        <div className="flex justify-between text-sm mb-2"><span className="font-medium">Progression globale</span><span className="font-semibold">{completion}%</span></div>
        <ProgressBar pct={completion} />
      </div>

      {showForm && (
        <div className="bg-card rounded-xl border border-border p-6 mb-6">
          <h3 className="font-display font-semibold mb-4">{editId ? "Modifier la tâche" : "Nouvelle tâche"}</h3>
          <div className="flex gap-2 mb-4">
            <button onClick={() => setForm({ ...form, taskType: "deadline" })} data-testid="type-deadline" className={`flex items-center gap-2 text-sm px-4 py-2 rounded-lg border ${form.taskType === "deadline" ? "bg-indigo-600 text-white border-indigo-600" : "bg-white border-border"}`}><Flag className="h-4 w-4" />Deadline (date limite)</button>
            <button onClick={() => setForm({ ...form, taskType: "scheduled" })} data-testid="type-scheduled" className={`flex items-center gap-2 text-sm px-4 py-2 rounded-lg border ${form.taskType === "scheduled" ? "bg-violet-600 text-white border-violet-600" : "bg-white border-border"}`}><CalendarClock className="h-4 w-4" />Planifiée (date & heure)</button>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label className="mb-1.5 block">Titre</Label><Input data-testid="task-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
            <div><Label className="mb-1.5 block">Responsable(s) — séparés par ;</Label><Input data-testid="task-responsible" placeholder="Marie Dupont ; Jean Martin" value={form.responsible} onChange={(e) => setForm({ ...form, responsible: e.target.value })} /></div>
            <div className="sm:col-span-2"><Label className="mb-1.5 block">Email(s) du/des responsable(s) — séparés par ;</Label><Input data-testid="task-responsible-email" placeholder="marie@x.fr ; jean@y.fr" value={form.responsibleEmail} onChange={(e) => setForm({ ...form, responsibleEmail: e.target.value })} /></div>
            <div className="sm:col-span-2"><Label className="mb-1.5 block">Description</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            {form.taskType === "deadline" ? (
              <>
                <div><Label className="mb-1.5 block">Date limite</Label><Input type="date" data-testid="task-deadline" value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })} /></div>
                <div><Label className="mb-1.5 block">Priorité</Label>
                  <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}><SelectTrigger data-testid="task-priority"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="high">Haute</SelectItem><SelectItem value="medium">Moyenne</SelectItem><SelectItem value="low">Basse</SelectItem></SelectContent></Select>
                </div>
              </>
            ) : (
              <>
                <div><Label className="mb-1.5 block">Date</Label><Input type="date" data-testid="task-scheduled-date" value={form.scheduledDate} onChange={(e) => setForm({ ...form, scheduledDate: e.target.value })} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="mb-1.5 block">Heure début</Label><Input type="time" data-testid="task-start" value={form.startTime} onChange={(e) => setForm({ ...form, startTime: e.target.value })} /></div>
                  <div><Label className="mb-1.5 block">Heure fin</Label><Input type="time" data-testid="task-end" value={form.endTime} onChange={(e) => setForm({ ...form, endTime: e.target.value })} /></div>
                </div>
              </>
            )}
            <div className="sm:col-span-2"><Label className="mb-1.5 block">Commentaires</Label><Textarea rows={2} value={form.comments === "NON_MODIFIABLE" ? "" : form.comments} onChange={(e) => setForm({ ...form, comments: e.target.value })} /></div>
          </div>
          <div className="flex justify-end gap-2 mt-4"><Button variant="outline" onClick={() => setShowForm(false)}>Annuler</Button><Button onClick={submit} data-testid="save-task">{editId ? "Enregistrer" : "Créer"}</Button></div>
        </div>
      )}

      <div className="flex items-center gap-6 border-b border-border mb-4">
        <button onClick={() => setTab("active")} data-testid="tab-active" className={`pb-2 text-sm font-semibold border-b-2 transition-colors ${tab === "active" ? "border-primary text-primary" : "border-transparent text-muted-foreground"}`}>En cours ({inProgress.length})</button>
        <button onClick={() => setTab("done")} data-testid="tab-done" className={`pb-2 text-sm font-semibold border-b-2 transition-colors ${tab === "done" ? "border-primary text-primary" : "border-transparent text-muted-foreground"}`}>Terminées ({done.length})</button>
      </div>

      <div className="flex flex-wrap gap-3 mb-4">
        <Select value={typeFilter} onValueChange={setTypeFilter}><SelectTrigger className="w-44" data-testid="filter-type"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Tous les types</SelectItem><SelectItem value="deadline">Deadlines</SelectItem><SelectItem value="scheduled">Planifiées</SelectItem></SelectContent></Select>
        <Select value={sortBy} onValueChange={setSortBy}><SelectTrigger className="w-52" data-testid="filter-sort"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="chrono">Tri : ordre chronologique</SelectItem><SelectItem value="priority">Tri : priorité</SelectItem></SelectContent></Select>
      </div>

      <div className="space-y-2">
        {list.length ? list.map((t) => {
          const u = t.taskType === "scheduled" ? null : taskUrgency(t.deadline);
          const locked = t.comments === "NON_MODIFIABLE";
          return (
            <div key={t.id} className="flex items-start gap-3 p-4 rounded-xl border border-border bg-card hover:shadow-sm transition-shadow" data-testid={`task-${t.id}`}>
              {t.taskType === "scheduled"
                ? <CalendarClock className="h-4 w-4 mt-1 shrink-0 text-violet-500" />
                : <span className={`h-2.5 w-2.5 rounded-full mt-1.5 shrink-0 ${PRIORITY_DOT[t.priority]}`} />}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-semibold">{t.title}</p>
                  {t.taskType === "scheduled"
                    ? <span className="text-xs px-2 py-0.5 rounded-full bg-violet-100 text-violet-700">Planifiée</span>
                    : <span className="text-xs text-muted-foreground">· {PRIO_LABEL[t.priority]}</span>}
                  {t.category && <span className="text-xs px-2 py-0.5 rounded-full bg-sky-50 text-sky-700" data-testid={`task-category-${t.id}`}>{t.category}</span>}
                  {u && <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${u.cls}`}>{u.label}</span>}
                </div>
                {t.description && <p className="text-sm text-muted-foreground line-clamp-2 mt-0.5">{t.description}</p>}
                <p className="text-xs text-muted-foreground mt-1">
                  {t.responsible && `👤 ${t.responsible}`}
                  {t.taskType === "scheduled" ? (t.scheduledDate && ` · 🗓 ${formatDate(t.scheduledDate)} ${t.startTime || ""}${t.endTime ? `–${t.endTime}` : ""}`) : (t.deadline && ` · 📅 ${formatDate(t.deadline)}`)}
                </p>
                {t.comments && !locked && <p className="text-xs bg-muted rounded-lg p-2 mt-2">{t.comments}</p>}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {locked ? (
                  <>
                    {t.status !== "done" && <Button size="icon" variant="ghost" onClick={() => complete(t)} data-testid={`complete-task-${t.id}`}><Check className="h-4 w-4 text-emerald-600" /></Button>}
                    <span className="text-xs text-muted-foreground flex items-center gap-1" data-testid={`task-locked-${t.id}`}><Lock className="h-3.5 w-3.5" />Auto.</span>
                  </>
                ) : (
                  <>
                    {t.status !== "done"
                      ? <Button size="icon" variant="ghost" onClick={() => complete(t)} data-testid={`complete-task-${t.id}`}><Check className="h-4 w-4 text-emerald-600" /></Button>
                      : <Button size="icon" variant="ghost" onClick={() => reopen(t)} data-testid={`reopen-task-${t.id}`}><RotateCcw className="h-4 w-4 text-amber-600" /></Button>}
                    <Button size="icon" variant="ghost" onClick={() => openEdit(t)} data-testid={`edit-task-${t.id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => remove(t.id)} data-testid={`delete-task-${t.id}`}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
                  </>
                )}
              </div>
            </div>
          );
        }) : <p className="text-sm text-muted-foreground text-center py-8">Aucune tâche.</p>}
      </div>

      <Dialog open={aiOpen} onOpenChange={setAiOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-indigo-600" />Assistant IA — Générer des tâches</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Textarea rows={3} data-testid="ai-brief" placeholder="Ex: J'organise une formation à Lyon le 15 juillet pour 30 personnes avec un traiteur et une salle de réception..." value={aiBrief} onChange={(e) => setAiBrief(e.target.value)} />
            <Button onClick={generateAi} disabled={aiLoading} className="w-full" data-testid="ai-generate"><Sparkles className="h-4 w-4 mr-2" />{aiLoading ? "Génération en cours..." : "Générer des tâches intelligentes"}</Button>
            {aiTasks.length > 0 && (
              <div className="space-y-2 max-h-[340px] overflow-y-auto">
                <p className="text-xs text-muted-foreground">Validez les tâches à ajouter :</p>
                {aiTasks.map((t, i) => (
                  <label key={i} className="flex items-start gap-2.5 p-2.5 rounded-lg border border-border cursor-pointer hover:bg-muted" data-testid={`ai-task-${i}`}>
                    <Checkbox checked={!!aiSel[i]} onCheckedChange={(c) => setAiSel((p) => ({ ...p, [i]: c }))} className="mt-0.5" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium" data-testid={`ai-task-title-${i}`}>{t.title}</p>
                      {t.description && <p className="text-xs text-muted-foreground" data-testid={`ai-task-description-${i}`}>{t.description}</p>}
                      {t.comment && <p className="text-[11px] text-slate-500 mt-1" data-testid={`ai-task-comment-${i}`}>À suivre : {t.comment}</p>}
                      <p className="text-[11px] text-muted-foreground mt-0.5" data-testid={`ai-task-meta-${i}`}>Catégorie {t.category || "administratif"} · priorité {t.priority} · échéance {t.dueDate || t.deadline || "—"}</p>
                    </div>
                  </label>
                ))}
                <Button onClick={addAiTasks} className="w-full" data-testid="ai-add-tasks">Ajouter les tâches sélectionnées</Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
